// src/app/state.ts — Store reactivo minimalista (sin framework) para Fase 1
import type { PlayerState, PlayerOptions } from '../entities/player.js';
import { createInitialState } from '../data/initialState.js';
import { loadSave, saveLocal, saveOptions, loadOptions } from './storage.js';

type Listener = (state: PlayerState) => void;
type OptionsListener = (opts: PlayerOptions) => void;

class GameStore {
  private state: PlayerState;
  private listeners = new Set<Listener>();
  private optionsListeners = new Set<OptionsListener>();

  constructor() {
    const saved = loadSave();
    this.state = saved ?? createInitialState();
    // Hidratar opciones si existen por separado
    const opts = loadOptions();
    if (opts) this.state.options = { ...this.state.options, ...opts };
  }

  get(): PlayerState {
    return this.state;
  }

  getOptions(): PlayerOptions {
    return this.state.options;
  }

  patch(partial: Partial<PlayerState>): void {
    this.state = { ...this.state, ...partial, meta: { ...this.state.meta, updatedAt: new Date().toISOString() } };
    this.emit();
    saveLocal(this.state);
  }

  setState(next: PlayerState): void {
    this.state = { ...next, meta: { ...next.meta, updatedAt: new Date().toISOString() } };
    this.emit();
    saveLocal(this.state);
  }

  updateOptions(patch: Partial<PlayerOptions>): void {
    this.state.options = { ...this.state.options, ...patch };
    this.emitOptions();
    saveOptions(this.state.options);
    // también persistir en save principal
    saveLocal(this.state);
  }

  reset(playerName = 'Explorador'): void {
    this.state = createInitialState(playerName);
    this.emit();
    saveLocal(this.state);
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => this.listeners.delete(fn);
  }

  subscribeOptions(fn: OptionsListener): () => void {
    this.optionsListeners.add(fn);
    fn(this.state.options);
    return () => this.optionsListeners.delete(fn);
  }

  private emit(): void {
    for (const fn of this.listeners) fn(this.state);
  }
  private emitOptions(): void {
    for (const fn of this.optionsListeners) fn(this.state.options);
  }
}

export const store = new GameStore();
